/*
 * Подготовка вложений перед отправкой.
 *
 * Фотография с телефона весит 3–8 МБ и содержит вчетверо больше пикселей,
 * чем нужно для переписки. Пережимаем в WebP: при качестве 0.9 разница с
 * оригиналом не видна глазом, а вес падает в несколько раз — это разгружает
 * и базу, и мобильный интернет на объекте.
 *
 * Файл заменяется только если стал заметно легче: пережимать и без того
 * маленькую картинку смысла нет, а испортить — можно. Исключение — смена
 * формата: если целевой формат не совпадает с исходным, конвертируем всегда,
 * невзирая на размер (иначе маленький файл уедет под чужим MIME).
 *
 * Формат и предел стороны — параметры: у чата свои умолчания (WebP,
 * 2560px), а фотографии замечаний обхода просят JPEG до 1600px — сервер
 * собирает PDF-протокол через pdfkit, который читает только PNG и JPEG.
 */

const QUALITY = 0.9;
/** Ниже этого размера не трогаем: выигрыш не окупает потерю. */
const SKIP_UNDER_BYTES = 200 * 1024;
/** Считаем пережатие удачным, если файл похудел хотя бы на пятую часть. */
const WORTH_IT = 0.8;
/** Расширение файла по целевому формату. */
const EXT: Record<'image/webp' | 'image/jpeg', string> = { 'image/webp': 'webp', 'image/jpeg': 'jpg' };

// Пропуск по размеру годится, только пока формат не меняется: если целевой
// формат отличается от исходного, конвертировать обязаны всегда — иначе
// маленький WebP уезжает под чужим MIME (см. addPhoto на сервере, который
// смотрит только на этот ярлык, а не на настоящие байты) и валит сборку
// PDF-протокола, который WebP не читает.
const canCompress = (file: File, format: 'image/webp' | 'image/jpeg'): boolean => {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return false;
  if (file.type !== format) return true;
  return file.size > SKIP_UNDER_BYTES;
};

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('не удалось прочитать картинку'));
    };
    img.src = url;
  });
}

/** Имя с новым расширением: «фото.jpg» → «фото.webp» (или «.jpg» для замечаний). */
const renamed = (name: string, ext: string): string => `${name.replace(/\.[^.]+$/, '')}.${ext}`;

export async function compressImage(
  file: File,
  format: 'image/webp' | 'image/jpeg' = 'image/webp',
  maxSide = 2560,
): Promise<File> {
  if (!canCompress(file, format)) return file;
  try {
    const img = await loadImage(file);
    const scale = Math.min(1, maxSide / Math.max(img.naturalWidth, img.naturalHeight));
    const width = Math.round(img.naturalWidth * scale);
    const height = Math.round(img.naturalHeight * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, format, QUALITY),
    );
    if (!blob) return file;
    // Проверка выигрыша по весу — только когда формат не меняется. При смене
    // формата отдаём сконвертированный файл всегда, даже если он вышел
    // тяжелее исходного: сервер проверяет только ярлык MIME, а не настоящие
    // байты, — правильный формат здесь важнее пары десятков килобайт.
    if (file.type === format && blob.size > file.size * WORTH_IT) return file;
    return new File([blob], renamed(file.name, EXT[format]), { type: format });
  } catch {
    // не вышло — отправляем как есть, это не повод терять фотографию
    return file;
  }
}

/**
 * Первый кадр видео — обложка для ленты. Без неё до загрузки самого файла
 * в пузырьке зияет чёрный прямоугольник.
 */
export function videoPoster(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    const finish = (blob: Blob | null) => {
      URL.revokeObjectURL(url);
      resolve(blob);
    };

    video.onloadeddata = () => {
      // отступаем от нулевой секунды: первый кадр часто чёрный
      video.currentTime = Math.min(0.6, (video.duration || 1) / 3);
    };
    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx || !canvas.width) return finish(null);
      ctx.drawImage(video, 0, 0);
      canvas.toBlob((b) => finish(b), 'image/webp', 0.8);
    };
    video.onerror = () => finish(null);
    video.src = url;
  });
}

/** Длительность видео или аудио в секундах — показывается на плитке. */
export function mediaDuration(file: File): Promise<number> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement(file.type.startsWith('video/') ? 'video' : 'audio');
    el.preload = 'metadata';
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(el.duration) ? Math.round(el.duration) : 0);
    };
    el.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(0);
    };
    el.src = url;
  });
}
